import { Calendar, Clock, MapPin, User } from "lucide-react";
import { Badge } from "@/components/ui/badge";

// Deterministic provider color palette — stable across sessions
const PROVIDER_PALETTE = [
  { bg: "bg-blue-100", text: "text-blue-800", border: "border-blue-200", dot: "bg-blue-500" },
  { bg: "bg-emerald-100", text: "text-emerald-800", border: "border-emerald-200", dot: "bg-emerald-500" },
  { bg: "bg-purple-100", text: "text-purple-800", border: "border-purple-200", dot: "bg-purple-500" },
  { bg: "bg-rose-100", text: "text-rose-800", border: "border-rose-200", dot: "bg-rose-500" },
  { bg: "bg-amber-100", text: "text-amber-800", border: "border-amber-200", dot: "bg-amber-500" },
  { bg: "bg-cyan-100", text: "text-cyan-800", border: "border-cyan-200", dot: "bg-cyan-500" },
  { bg: "bg-pink-100", text: "text-pink-800", border: "border-pink-200", dot: "bg-pink-500" },
  { bg: "bg-lime-100", text: "text-lime-800", border: "border-lime-200", dot: "bg-lime-500" },
  { bg: "bg-indigo-100", text: "text-indigo-800", border: "border-indigo-200", dot: "bg-indigo-500" },
];

export function providerColor(key) {
  if (!key) return PROVIDER_PALETTE[0];
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) % 997;
  return PROVIDER_PALETTE[hash % PROVIDER_PALETTE.length];
}

export default function ProviderAppointmentCard({ appointment: a }) {
  const key = a.practitioner_id || a.practitioner_name || "";
  const color = providerColor(key);
  const statusLower = (a.status || "").toLowerCase();

  return (
    <div
      className={`bg-card border ${color.border} border-l-4 rounded-xl p-4 space-y-2 shadow-sm hover:shadow-md transition-shadow`}
      data-testid={`provider-appointment-${a.id || a.intakeq_id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
            <User className="w-3 h-3" /> Patient
          </div>
          <h4 className="font-semibold text-base truncate" data-testid="patient-name">{a.patient_name || "Patient"}</h4>
          <p className="text-xs text-muted-foreground truncate">{a.patient_email || ""}</p>
        </div>
        {statusLower && (
          <Badge variant={statusLower === "confirmed" ? "default" : "outline"} className="shrink-0 capitalize text-[10px]">
            {a.status}
          </Badge>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground pt-1 border-t border-border/60">
        <span className="inline-flex items-center gap-1">
          <Calendar className="w-3.5 h-3.5" /> {a.date || "TBD"}
        </span>
        {a.time && (
          <span className="inline-flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" /> {a.time}
          </span>
        )}
        {a.location && (
          <span className="inline-flex items-center gap-1">
            <MapPin className="w-3.5 h-3.5" /> {a.location}
          </span>
        )}
        <span className="ml-auto text-foreground font-medium">{a.appointment_type}</span>
      </div>

      <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${color.bg} ${color.text}`} data-testid="provider-badge">
        <span className={`w-1.5 h-1.5 rounded-full ${color.dot}`} />
        {a.practitioner_name || "Provider"}
      </div>
    </div>
  );
}
